import React from "react";
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import { OrangeTextButton } from "../../common/OrangeTextButton";
import { yyyymmddGenerator } from "../Analytics/AnalyticsUtils/yyyymmddGenerator";
import {
  createEmptySelection,
  createSelectionFromTimes,
  dateFromMinutes,
  getCalendarEventTimesForAPI,
  getRangeFromSelection,
  hasInvalidSelection,
  isCalendarEventAlwaysOn,
  minutesFromApiTime,
  minutesFromDate,
  normalizeCalendarEventTimes,
  parseCalendarEventDate,
} from "./AddEventDialogUtils";

export type CalendarEventFormValues = {
  event_date: string;
  name: string;
  start_time: string | null;
  end_time: string | null;
};

const hourLabels = [
  "12",
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "10",
  "11",
  "12",
  "1",
  "2",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "10",
  "11",
];

export const AddEventDialog = ({
  open,
  onCancel,
  onSubmit,
  title = "Add Event",
  submitLabel = "Submit",
  initialEvent,
  requireChanges = false,
  showScheduleTime = true,
}: {
  open: boolean;
  onCancel: () => void;
  onSubmit: (newEvent: CalendarEventFormValues) => void;
  title?: string;
  submitLabel?: string;
  initialEvent?: CalendarEventFormValues;
  requireChanges?: boolean;
  showScheduleTime?: boolean;
}) => {
  const dialogMaxWidth = showScheduleTime ? "lg" : "sm";

  const createMidnightDate = () => {
    const midnight = new Date();
    midnight.setHours(0, 0, 0, 0);
    return midnight;
  };
  const createFilledSelection = () => Array.from({ length: 96 }, () => true);

  const [eventDate, setEventDate] = React.useState(new Date());
  const [eventName, setEventName] = React.useState("");
  const [selectedSlots, setSelectedSlots] = React.useState<boolean[]>(
    createEmptySelection()
  );
  const [startTime, setStartTime] = React.useState<Date | null>(
    createMidnightDate()
  );
  const [endTime, setEndTime] = React.useState<Date | null>(createMidnightDate());
  const [isMouseDown, setIsMouseDown] = React.useState(false);
  const [dragStartSlot, setDragStartSlot] = React.useState<number | null>(null);
  const [hoverSlot, setHoverSlot] = React.useState<number | null>(null);
  const [detectionsAlwaysOn, setDetectionsAlwaysOn] = React.useState(true);
  const [hasChanges, setHasChanges] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      return;
    }

    if (initialEvent) {
      const alwaysOn = isCalendarEventAlwaysOn(
        initialEvent.start_time,
        initialEvent.end_time
      );

      setEventDate(parseCalendarEventDate(initialEvent.event_date));
      setEventName(initialEvent.name);
      setDetectionsAlwaysOn(alwaysOn);
      setIsMouseDown(false);
      setDragStartSlot(null);
      setHoverSlot(null);

      if (alwaysOn) {
        setSelectedSlots(createEmptySelection());
        setStartTime(createMidnightDate());
        setEndTime(createMidnightDate());
      } else {
        const startMinutes = minutesFromApiTime(initialEvent.start_time!);
        const endMinutes = minutesFromApiTime(initialEvent.end_time!);
        const selection = createSelectionFromTimes(startMinutes, endMinutes);
        const baseDate = new Date();

        setSelectedSlots(selection);
        setStartTime(dateFromMinutes(baseDate, startMinutes));
        setEndTime(dateFromMinutes(baseDate, endMinutes));
      }
    } else {
      setEventDate(new Date());
      setEventName("");
      setSelectedSlots(createEmptySelection());
      setStartTime(createMidnightDate());
      setEndTime(createMidnightDate());
      setIsMouseDown(false);
      setDragStartSlot(null);
      setHoverSlot(null);
      setDetectionsAlwaysOn(true);
    }

    setHasChanges(false);
  }, [
    open,
    initialEvent?.event_date,
    initialEvent?.name,
    initialEvent?.start_time,
    initialEvent?.end_time,
  ]);

  React.useEffect(() => {
    if (!open || !initialEvent || !requireChanges) {
      setHasChanges(false);
      return;
    }

    const currentValues = {
      event_date: yyyymmddGenerator(eventDate),
      name: eventName.trim(),
      ...(showScheduleTime
        ? getCalendarEventTimesForAPI(detectionsAlwaysOn, startTime, endTime)
        : {}),
    };
    const initialValues = {
      event_date: initialEvent.event_date,
      name: initialEvent.name.trim(),
      ...(showScheduleTime
        ? normalizeCalendarEventTimes(
            initialEvent.start_time,
            initialEvent.end_time
          )
        : {}),
    };

    setHasChanges(
      currentValues.event_date !== initialValues.event_date ||
        currentValues.name !== initialValues.name ||
        (showScheduleTime &&
          (currentValues.start_time !== initialValues.start_time ||
            currentValues.end_time !== initialValues.end_time))
    );
  }, [
    open,
    initialEvent,
    requireChanges,
    showScheduleTime,
    eventDate,
    eventName,
    detectionsAlwaysOn,
    startTime,
    endTime,
  ]);

  const updateTimesFromSelection = (nextSelection: boolean[]) => {
    const range = getRangeFromSelection(nextSelection);
    if (!range) {
      setStartTime(null);
      setEndTime(null);
      return;
    }

    const baseDate = new Date();
    setStartTime(dateFromMinutes(baseDate, range.startMinutes));
    setEndTime(dateFromMinutes(baseDate, range.endMinutes));
  };

  const applySelection = (nextSelection: boolean[], syncTimes = true) => {
    if (hasInvalidSelection(nextSelection)) {
      alert("Only one start and end time are supported.");
      return;
    }

    setSelectedSlots(nextSelection);
    if (syncTimes) {
      updateTimesFromSelection(nextSelection);
    }
  };

  const toggleRange = (startIndex: number, endIndex: number) => {
    const nextSelection = [...selectedSlots];
    const min = Math.min(startIndex, endIndex);
    const max = Math.max(startIndex, endIndex);
    for (let i = min; i <= max; i++) {
      nextSelection[i] = !nextSelection[i];
    }
    applySelection(nextSelection);
  };

  const handleStartTimeChange = (nextStart: Date | null) => {
    setStartTime(nextStart);
    if (!nextStart || !endTime) {
      return;
    }
    applySelection(
      createSelectionFromTimes(minutesFromDate(nextStart), minutesFromDate(endTime)),
      false
    );
  };

  const handleEndTimeChange = (nextEnd: Date | null) => {
    setEndTime(nextEnd);
    if (!startTime || !nextEnd) {
      return;
    }
    applySelection(
      createSelectionFromTimes(
        minutesFromDate(startTime),
        minutesFromDate(nextEnd)
      ),
      false
    );
  };

  const isPreviewCell = (slotIndex: number) => {
    if (!isMouseDown || dragStartSlot === null || hoverSlot === null) {
      return false;
    }
    const min = Math.min(dragStartSlot, hoverSlot);
    const max = Math.max(dragStartSlot, hoverSlot);
    return slotIndex >= min && slotIndex <= max;
  };

  const displayedSlots = detectionsAlwaysOn
    ? Array.from({ length: 96 }, () => true)
    : selectedSlots;
  const displayedStartTime = detectionsAlwaysOn ? createMidnightDate() : startTime;
  const displayedEndTime = detectionsAlwaysOn ? createMidnightDate() : endTime;

  return (
    <Dialog open={open} onClose={onCancel} maxWidth={dialogMaxWidth} fullWidth>
      <DialogTitle sx={{ fontFamily: "Mulish" }}>{title}</DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <div className="mt-2 flex flex-col gap-4">
            <div className="flex gap-[20px]">
              <DatePicker
                label="Event Date"
                value={eventDate}
                onChange={(newValue) => {
                  if (newValue) {
                    setEventDate(newValue);
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} sx={{ fontFamily: "Mulish", width: 200 }} />
                )}
              />
              <TextField
                label="Event Name"
                variant="standard"
                value={eventName}
                onChange={(event) => setEventName(event.target.value)}
                sx={{ fontFamily: "Mulish", width: 500 }}
              />
            </div>
            {showScheduleTime && (
              <>
                <div> </div>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={detectionsAlwaysOn}
                      onChange={(event) => {
                        const checked = event.target.checked;
                        setDetectionsAlwaysOn(checked);
                        if (!checked) {
                          setSelectedSlots(createFilledSelection());
                        }
                      }}
                    />
                  }
                  label="Detections Always On (Default)"
                  sx={{ "& .MuiFormControlLabel-label": { fontFamily: "Mulish" } }}
                />

                <Table size="small" sx={{ tableLayout: "fixed" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        colSpan={48}
                        align="center"
                        sx={{
                          fontFamily: "Mulish",
                          borderTop: "1px solid #9CA3AF",
                          borderBottom: "1px solid #9CA3AF",
                          borderLeft: "1px solid #9CA3AF",
                          borderRight: "2px solid #9CA3AF",
                        }}
                      >
                        AM
                      </TableCell>
                      <TableCell
                        colSpan={48}
                        align="center"
                        sx={{
                          fontFamily: "Mulish",
                          borderTop: "1px solid #9CA3AF",
                          borderBottom: "1px solid #9CA3AF",
                          borderRight: "1px solid #9CA3AF",
                          borderLeft: "2px solid #9CA3AF",
                        }}
                      >
                        PM
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      {hourLabels.map((label, index) => (
                        <TableCell
                          key={`hour-${index}`}
                          colSpan={4}
                          align="center"
                          sx={{
                            fontFamily: "Mulish",
                            fontSize: "12px",
                            padding: "4px 0",
                            borderBottom: "1px solid #D1D5DB",
                            borderLeft:
                              index === 12
                                ? "2px solid #9CA3AF"
                                : "1px solid #9CA3AF",
                            borderRight: "1px solid #9CA3AF",
                          }}
                        >
                          {label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      {displayedSlots.map((isActive, slotIndex) => {
                        const previewCell = isPreviewCell(slotIndex);
                        return (
                          <TableCell
                            data-testid={`event-slot-${slotIndex}`}
                            key={`slot-${slotIndex}`}
                            onMouseDown={() => {
                              if (detectionsAlwaysOn) {
                                return;
                              }
                              setIsMouseDown(true);
                              setDragStartSlot(slotIndex);
                              setHoverSlot(slotIndex);
                            }}
                            onMouseOver={() => {
                              if (detectionsAlwaysOn) {
                                return;
                              }
                              if (isMouseDown) {
                                setHoverSlot(slotIndex);
                              }
                            }}
                            onMouseUp={() => {
                              if (detectionsAlwaysOn) {
                                return;
                              }
                              if (dragStartSlot === null) {
                                return;
                              }
                              toggleRange(dragStartSlot, slotIndex);
                              setIsMouseDown(false);
                              setDragStartSlot(null);
                              setHoverSlot(null);
                            }}
                            sx={{
                              cursor: detectionsAlwaysOn ? "default" : "pointer",
                              padding: "20px 2px",
                              backgroundColor: isActive
                                ? detectionsAlwaysOn
                                  ? "#A5D6A7"
                                  : "#66BB6A"
                                : previewCell
                                ? "#FFD6A6"
                                : "#F3F4F6",
                              border: "1px solid #D1D5DB",
                              borderLeft:
                                slotIndex === 48
                                  ? "2px solid #9CA3AF"
                                  : slotIndex % 4 === 0
                                  ? "1px solid #9CA3AF"
                                  : "1px solid #D1D5DB",
                            }}
                          />
                        );
                      })}
                    </TableRow>
                  </TableBody>
                </Table>
                {!detectionsAlwaysOn && (
                  <div>
                    <OrangeTextButton
                      onClick={() => {
                        applySelection(createEmptySelection());
                      }}
                      text="Clear Grid"
                      disabled={false}
                    />
                  </div>
                )}
                <div> </div>
                <div className="flex gap-[10px]">
                  <TimePicker
                    label="Start Time"
                    value={displayedStartTime}
                    onChange={handleStartTimeChange}
                    disabled={detectionsAlwaysOn}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        inputProps={{
                          ...params.inputProps,
                          "data-testid": "start-time-input",
                        }}
                        sx={{ fontFamily: "Mulish", width: 250 }}
                      />
                    )}
                  />
                  <TimePicker
                    label="End Time"
                    value={displayedEndTime}
                    onChange={handleEndTimeChange}
                    disabled={detectionsAlwaysOn}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        inputProps={{
                          ...params.inputProps,
                          "data-testid": "end-time-input",
                        }}
                        sx={{ fontFamily: "Mulish", width: 250 }}
                      />
                    )}
                  />
                </div>
              </>
            )}
          </div>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions sx={{ paddingRight: 3, paddingBottom: 2 }}>
        <OrangeTextButton onClick={onCancel} text="Cancel" disabled={false} />
        <OrangeTextButton
          onClick={() => {
            const scheduleTimeValues = showScheduleTime
              ? getCalendarEventTimesForAPI(detectionsAlwaysOn, startTime, endTime)
              : initialEvent
              ? normalizeCalendarEventTimes(
                  initialEvent.start_time,
                  initialEvent.end_time
                )
              : { start_time: null, end_time: null };

            onSubmit({
              event_date: yyyymmddGenerator(eventDate),
              name: eventName.trim(),
              ...scheduleTimeValues,
            });
          }}
          text={submitLabel}
          disabled={!eventName.trim() || (requireChanges && !hasChanges)}
        />
      </DialogActions>
    </Dialog>
  );
};
